import { useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal "Revisar ortografía" — pasa el guion completo a la IA y muestra
 * las erratas detectadas con sugerencia + contexto. El usuario acepta
 * o rechaza cada una individualmente. Al aplicar, se sustituye en el
 * elemento concreto preservando el resto del texto.
 *
 * Vive fuera del editor protegido (`/app/frontend/src/components/screenplay/**`).
 */
export default function SpellcheckModal({ open, onClose, projectId, screenplay, onApplied }) {
  const [state, setState] = useState({ phase: "idle", error: "", issues: [] });
  // Selección del usuario: para cada índice de issue, true = aplicar, false = ignorar
  const [accepted, setAccepted] = useState({});

  const scan = async () => {
    setState({ phase: "scanning", error: "", issues: [] });
    try {
      const r = await api.post(`/icaa/projects/${projectId}/screenplay/spellcheck`);
      const issues = r.data?.issues || [];
      const initialAccepted = Object.fromEntries(issues.map((_, i) => [i, true]));
      setAccepted(initialAccepted);
      setState({
        phase: "review",
        error: "",
        issues,
        checked_elements: r.data?.checked_elements || 0,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al revisar ortografía";
      setState({ phase: "idle", error: String(msg), issues: [] });
    }
  };

  const toggle = (i) => setAccepted((p) => ({ ...p, [i]: !p[i] }));

  const apply = async () => {
    const toApply = state.issues.filter((_, i) => accepted[i]);
    if (toApply.length === 0) {
      reset();
      onClose?.();
      return;
    }
    setState({ ...state, phase: "applying" });
    // Agrupar issues por element_id y aplicar todas las sustituciones
    // (string replace simple) sobre el texto del elemento, preservando el resto.
    const byEl = {};
    for (const it of toApply) {
      if (!byEl[it.element_id]) byEl[it.element_id] = [];
      byEl[it.element_id].push(it);
    }
    const newElements = (screenplay.elements || []).map((el) => {
      const fixes = byEl[el.id];
      if (!fixes) return el;
      let txt = el.text || "";
      for (const f of fixes) {
        // Reemplazo case-sensitive de la primera ocurrencia. Si no hay match
        // (palabra ya corregida o variante), saltamos sin error.
        if (txt.includes(f.original)) {
          txt = txt.replace(f.original, f.suggestion);
        }
      }
      return { ...el, text: txt };
    });
    try {
      const updated = { ...screenplay, elements: newElements };
      await api.put(`/icaa/projects/${projectId}/screenplay`, updated);
      onApplied?.(updated);
      reset();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al aplicar";
      setState({ phase: "review", error: String(msg), issues: state.issues, checked_elements: state.checked_elements });
    }
  };

  const reset = () => {
    setState({ phase: "idle", error: "", issues: [] });
    setAccepted({});
  };

  if (!open) return null;
  const acceptedCount = Object.values(accepted).filter(Boolean).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => { if (state.phase !== "applying" && state.phase !== "scanning") { reset(); onClose?.(); } }}
      data-testid="spellcheck-modal"
    >
      <div
        className="relative flex h-[88vh] w-[860px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Revisar ortografía
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              La IA marca erratas detectadas en el guion entero
            </p>
          </div>
          <button
            onClick={() => { if (state.phase !== "applying" && state.phase !== "scanning") { reset(); onClose?.(); } }}
            data-testid="spellcheck-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620] hover:text-[#941620]"
          >
            ✕ Cerrar
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {state.phase === "idle" && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="text-[3rem]">🔤</div>
              <h3 className="mt-3 font-serif text-2xl text-stone-900">Corrige tu guion en bloque</h3>
              <p className="mt-3 max-w-md text-[13px] leading-relaxed text-stone-600">
                La IA escanea todas las líneas del guion (acción, diálogo, acotación, escena, nota, sobreimpresión) y detecta erratas, tildes mal puestas y problemas de puntuación en español de España.
              </p>
              <p className="mt-2 max-w-md text-[12px] leading-relaxed text-stone-500">
                Las líneas tipo PERSONAJE no se revisan (puede haber muletillas, vulgarismos o anglicismos intencionados en diálogos).
              </p>
              {state.error && (
                <p className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">{state.error}</p>
              )}
              <button
                onClick={scan}
                data-testid="spellcheck-scan"
                className="mt-6 rounded-full bg-[#941620] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019]"
              >
                Revisar ahora
              </button>
            </div>
          )}

          {(state.phase === "scanning" || state.phase === "applying") && (
            <div className="flex h-full flex-col items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
              <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">
                {state.phase === "scanning" ? "Revisando con IA…" : "Aplicando correcciones…"}
              </p>
            </div>
          )}

          {state.phase === "review" && (
            <div className="space-y-3" data-testid="spellcheck-issues">
              {state.error && (
                <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">{state.error}</p>
              )}
              <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-[11px] text-stone-700">
                <strong>{state.issues.length}</strong> errata{state.issues.length === 1 ? "" : "s"} detectada{state.issues.length === 1 ? "" : "s"} · revisados <strong>{state.checked_elements}</strong> bloques
              </div>

              {state.issues.length === 0 ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-900">
                  ✓ El guion no tiene erratas detectadas.
                </div>
              ) : (
                <ul className="space-y-2">
                  {state.issues.map((it, i) => (
                    <li
                      key={i}
                      className={`rounded-lg border px-3 py-2 transition ${
                        accepted[i] ? "border-[#941620]/40 bg-[#941620]/5" : "border-stone-200 bg-stone-50 opacity-70"
                      }`}
                      data-testid={`spellcheck-issue-${i}`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!accepted[i]}
                          onChange={() => toggle(i)}
                          data-testid={`spellcheck-toggle-${i}`}
                          className="mt-1 h-4 w-4 accent-[#941620]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="rounded bg-rose-100 px-1.5 py-0.5 font-mono text-[12px] font-semibold text-rose-800 line-through">
                              {it.original}
                            </span>
                            <span className="text-stone-400">→</span>
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-[12px] font-semibold text-emerald-800">
                              {it.suggestion}
                            </span>
                          </div>
                          {it.context && (
                            <p className="mt-1 text-[11px] italic text-stone-500 line-clamp-2">
                              «…{it.context}…»
                            </p>
                          )}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {state.phase === "review" && (
          <footer className="border-t border-stone-200 bg-white px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-stone-600">
                {acceptedCount === 0
                  ? "No hay correcciones marcadas."
                  : `${acceptedCount} corrección${acceptedCount === 1 ? "" : "es"} pendiente${acceptedCount === 1 ? "" : "s"} de aplicar.`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { reset(); onClose?.(); }}
                  className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-stone-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={apply}
                  disabled={acceptedCount === 0}
                  data-testid="spellcheck-apply"
                  className="rounded-full bg-[#941620] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019] disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  Aplicar al guion
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
